import { Fragment, useState, useEffect, useRef } from 'react';
import { Dialog, Popover, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { LoggedRoutes } from '../../routes';
import { useAuth } from '../../Auth';
import { useNavigate } from 'react-router-dom';
import { IoIosLock } from "react-icons/io";
import ReactDOM from 'react-dom';

import './PortalHeader.css'

// Icons
import { LuUserRound } from "react-icons/lu";
import { IoChatbubblesOutline } from "react-icons/io5";

const SUPPORT_PATH = '/fnn_portal_support';

// Any sub-route whose name mentions "ticket" is hidden from the menus,
// since ticket submission now lives behind the dedicated Support button.
const isTicketRoute = (name: string) => /ticket/i.test(name);


export default function Header() {

    // ------- Section of the code responsible for making the navbar visible whenever the user scrolls down ------- //
    const [showHeader, setShowHeader] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [scrolledUp, setScrolledUp] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    // For FNN backend
    const { isAuthenticated, organization } = useAuth();

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            // Show header only if scrolling up
            if (currentScrollY < lastScrollY && currentScrollY > 0) {
                setShowHeader(true);
                setScrolledUp(true);
            } else if (currentScrollY === 0) {
                setShowHeader(true);
                setScrolledUp(false); // at top, transparent
            } else {
                setShowHeader(false); // scrolling down
            }

            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [lastScrollY]);

    // ------- Section of the code responsible for making the navbar visible whenever the user scrolls down ------- //

    const navigate = useNavigate();

    const { logout, username, first_name, last_name, organization_permissions, is_staff } = useAuth();

    // Checking permissions
    const normalizedPermissions = organization_permissions.map(p => p.toLowerCase());

    const hasEsri = normalizedPermissions.includes('esri dashboard');
    const hasDetectorMap = normalizedPermissions.includes('detector map'); // name in navbar
    const hasManual = normalizedPermissions.includes('installation manual');
    const hasTutorial = normalizedPermissions.includes('tutorial videos');
    const hasContact = normalizedPermissions.includes('fnn contact');
    const hasFaq = normalizedPermissions.includes('faq');
    const hasSupport = hasContact; // same permission the old "Submit Ticket" link used
    const [showPopup, setShowPopup] = useState(false);

    const renderSidebarButton = (label: string, path: string, hasPermission: boolean) => (
        <button
            className={`sidebar-btn1 ${!hasPermission ? 'disabled-link' : ''}`}
            onClick={() => {
                if (hasPermission) {
                    window.location.href = path;
                } else {
                    setShowPopup(true);
                }
            }}
        >
            {label}
        </button>
    );

    // Prominent Support button (desktop header + mobile drawer)
    const handleSupportClick = () => {
        setMenuOpen(false);
        if (hasSupport) {
            navigate(SUPPORT_PATH);
        } else {
            setShowPopup(true);
        }
    };

    const renderSupportButton = (extraClass = '') => (
        <button
            type="button"
            onClick={handleSupportClick}
            className={`support-btn ${extraClass}`}
            aria-label="Get support"
        >
            <IoChatbubblesOutline className="support-btn-icon" aria-hidden="true" />
            <span>Help Desk</span>
            {!hasSupport && <IoIosLock className="support-btn-lock" aria-hidden="true" />}
        </button>
    );

    const LogoWhite = require('../../pages/HeaderFolder/img/FNNLogoWithWhiteFont-07.png');
    const LogoBlack = require('../../pages/HeaderFolder/img/FNNLogoWithBlackFont-06.png');

    const [menuOpen, setMenuOpen] = useState(false);
    const handleAuthRedirection = () => {
        navigate('/login'); // Replace with your desired route
    };

    /* New menu */
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);

    const toggleProfileMenu = () => {
        setShowProfileMenu(prev => !prev);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setShowProfileMenu(false);
        }
    };

    function logoutAndClosePop() {
        toggleProfileMenu();
        logout();
    }

    useEffect(() => {
        if (showProfileMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showProfileMenu]);



    return (
        <Popover
            className={`header fixed top-0 w-full transition-all duration-300
  ${showHeader ? 'translate-y-0' : '-translate-y-full'} 
  ${(scrolledUp || isHovered) ? 'bg-white bg-opacity-90' : 'bg-white bg-opacity-90'}`}
            style={{ zIndex: '10' }}
        >

            <div className="header-wrapper">

                {/* Centered main nav */}
                <div
                    className="testing-header"
                    onMouseLeave={() => setIsHovered(false)}
                >

                    <Link to="/" className="header-left-side">
                        <img className="fnn-header-logo" src={LogoBlack} alt="" />
                    </Link>

                    <ul className="tesing-ul">
                        {LoggedRoutes.map((route, index) =>

                            <div key={index} className="relative group" onMouseEnter={() => setIsHovered(true)}>
                                <div className={`cursor-default header-text`}>
                                    {route.name}
                                </div>
                            </div>
                        )}
                    </ul>

                    <div className={`sub-item-div-for-logged ${isHovered ? 'show' : ''}`}>
                        <div className='sidebar-container'>
                            {renderSidebarButton('Esri Dashboard', '/fnn_portal_esri', hasEsri)}
                            {renderSidebarButton('Product Suite', '/fnn_portal_product_suite', true)}
                        </div>

                        <div className="sidebar-container">
                            {renderSidebarButton('Detector Information', '/fnn_portal_map', hasDetectorMap)}
                            {renderSidebarButton('Installation Manual', '/fnn_portal_manual', hasManual)}
                            {renderSidebarButton('Tutorial Videos', '/fnn_portal_video_hub', hasTutorial)}
                        </div>

                        <div className='sidebar-container'>
                            {renderSidebarButton('FNN Contact', '/fnn_portal_representative', hasContact)}
                            {renderSidebarButton('FAQs', '/fnn_portal_faqs', hasFaq)}
                        </div>
                    </div>



                    {/* Right side: support + auth + menu */}
                    <div className="header-right-side">

                        <div className="header-actions">
                            {renderSupportButton('support-btn--desktop')}

                            <div
                                onClick={toggleProfileMenu}
                                className="logged-user-icon-and-name"
                            >
                                <LuUserRound className='logged-user-icon' />
                                {first_name !== "" ? first_name : username.split("@")[0]}
                            </div>
                        </div>

                        {/* Mobile-only: icon-only Help Desk button beside the menu bars */}
                        <button
                            type="button"
                            onClick={handleSupportClick}
                            className="support-icon-btn"
                            aria-label="Help Desk"
                        >
                            <IoChatbubblesOutline aria-hidden="true" />
                            {!hasSupport && <IoIosLock className="support-icon-btn-lock" aria-hidden="true" />}
                        </button>

                        <div
                            onClick={() => setMenuOpen(!menuOpen)}
                            className="header-menu"
                        >
                            <Bars3Icon className="h- w-5" aria-hidden="true" />
                        </div>
                    </div>
                </div>

                {/* Conditional menu */}
                {showProfileMenu && (
                    <div className="logged-user-sidebar" ref={menuRef}>
                        <div>
                            <h1>{first_name !== "" ? first_name : username.split("@")[0]} {last_name}</h1>
                            <p>{username.split('@')[0]}</p>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <Link
                                to="/fnn_portal_profile"
                                className="logged-user-sidebar-btn"
                                style={{ textDecoration: 'none', color: 'black' }}
                            >
                                My Account
                            </Link>

                            {organization === "FNN" &&
                                <a
                                    href="/team_portal"
                                    className="logged-user-sidebar-btn"
                                    style={{ textDecoration: 'none', color: 'black' }}
                                >
                                    Team Portal
                                </a>
                            }

                            <Link
                                to="/login"
                                className="logged-user-sidebar-btn"
                                onClick={logoutAndClosePop}
                                style={{ textDecoration: 'none', color: 'black' }}
                            >
                                Log Out
                            </Link>


                        </div>
                    </div>
                )}

            </div>

            <Transition.Root show={menuOpen} as={Fragment}>
                <Dialog as="div" className="relative z-[100]" onClose={setMenuOpen}>
                    <div className="fixed inset-0" />

                    <div className="fixed inset-0 overflow-hidden">
                        <div className="absolute inset-0 overflow-hidden">
                            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                                <Transition.Child
                                    as={Fragment}
                                    enter="transform transition ease-in-out duration-500 sm:duration-700"
                                    enterFrom="translate-x-full"
                                    enterTo="translate-x-0"
                                    leave="transform transition ease-in-out duration-500 sm:duration-700"
                                    leaveFrom="translate-x-0"
                                    leaveTo="translate-x-full"
                                >
                                    <Dialog.Panel className="pointer-events-auto w-screen h-full max-w-md">
                                        <div className="menu-side-bar ">
                                            <div className="px-4 sm:px-6">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex h-7 items-center">
                                                        <button
                                                            type="button"
                                                            className="rounded-md text-gray-400 hover:text-red-600 focus:outline-none cursor-pointer"
                                                            onClick={() => setMenuOpen(false)}
                                                        >
                                                            <XMarkIcon
                                                                className="h-6 w-6"
                                                                aria-hidden="true"
                                                            />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="relative mt-2 flex-1 w-full px-6 sm:px-6 overflow-y-auto max-h-[90dvh]" style={{ maxHeight: '90dvh' }}>

                                                {/* Support gets top billing in the mobile drawer */}
                                                {renderSupportButton('support-btn--mobile')}

                                                <ul className="my-menu-ul">
                                                    {LoggedRoutes.map((menuItem, index) => (
                                                        <li key={index} className="menu-item" onClick={() => setMenuOpen(false)}>
                                                            <span className="menu-link nothing">{menuItem.name}</span>

                                                            {menuItem.subRoutes && (
                                                                <ul className="sub-menu">
                                                                    {menuItem.subRoutes
                                                                        .filter(subItem => !isTicketRoute(subItem.name))
                                                                        .map((subItem, subIndex) => {
                                                                        const linkText = subItem.name;

                                                                        // Map permissions based on label
                                                                        const permissionsMap: Record<string, boolean> = {
                                                                            'Esri Dashboard': hasEsri,
                                                                            'Product Suite': true,
                                                                            'Detector Information': hasDetectorMap,
                                                                            'Installation Manual': hasManual,
                                                                            'Tutorial Videos': hasTutorial,
                                                                            'FNN Contact': hasContact,
                                                                            'FAQs': hasFaq,
                                                                        };

                                                                        const hasPermission = permissionsMap[subItem.name] ?? true;

                                                                        return (
                                                                            <li key={subIndex} className="sub-menu-item">
                                                                                <button
                                                                                    className={`menu-link sub ${!hasPermission ? 'disabled-link' : ''}`}
                                                                                    onClick={() => {
                                                                                        if (hasPermission) {
                                                                                            window.location.href = subItem.to;
                                                                                        } else {
                                                                                            setShowPopup(true);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {linkText}
                                                                                    {!hasPermission && <IoIosLock />}
                                                                                </button>
                                                                            </li>
                                                                        );
                                                                    })}
                                                                </ul>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>


                                                <div className='logged-user-icon-and-name-for-mobile-box'>
                                                    <div className="logged-user-icon-and-name-for-mobile">
                                                        <LuUserRound className='logged-user-icon' />
                                                        {first_name !== "" ? first_name : username.split("@")[0]}
                                                    </div>


                                                    <div className='making-it-invisible'>
                                                        <Link
                                                            to="/fnn_portal_profile"
                                                            className="logged-user-sidebar-btn"
                                                            style={{ textDecoration: 'none', color: 'black' }}
                                                        >
                                                            My Account
                                                        </Link>

                                                        {organization === "FNN" &&
                                                            <a
                                                                href="/team_portal"
                                                                className="logged-user-sidebar-btn"
                                                                style={{ textDecoration: 'none', color: 'black' }}
                                                            >
                                                                Team Portal
                                                            </a>
                                                        }

                                                        <Link
                                                            to="/login"
                                                            className="logged-user-sidebar-btn"
                                                            onClick={logoutAndClosePop}
                                                            style={{ textDecoration: 'none', color: 'black' }}
                                                        >
                                                            Log Out
                                                        </Link>
                                                    </div>
                                                </div>



                                            </div>

                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {showPopup && ReactDOM.createPortal(
                <div className="suite-popup-overlay2">
                    <div className="suite-popup-content">
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }} className='faq-adjust-buttons suite-extension'>
                            <button
                                style={{ paddingLeft: '1rem', paddingRight: '1rem' }}
                                onClick={() => setShowPopup(false)}
                            >
                                <strong>Back</strong>
                            </button>
                        </div>

                        <h1>You Do Not Have Access to This Feature</h1>
                        <IoIosLock className='lock-icon-popup' />
                        <p>This feature is part of a higher-tier plan and isn’t available with your current subscription.</p>
                        <p>To unlock access, you can speak with an FNN representative to discuss your options.</p>
                        <Link to='/fnn_portal_representative'>
                            <button className='suite-popup-content-btn'>Contact Us</button>
                        </Link>
                    </div>
                </div>,
                document.body
            )}

        </Popover>
    );
}